export interface MetricasCuadro {
  drawCalls: number;
  triangulos: number;
  programas: number;
  /** El cuadro incluyo el repintado del shadow map, que es un pase extra completo. */
  conSombra: boolean;
}

export interface MetricasActualizacion {
  /** Tiempo que `fragments.update()` bloquea el hilo principal antes de devolver la promesa. */
  bloqueoMs: number;
  /** Tiempo total hasta que la promesa resuelve, worker incluido. */
  totalMs: number;
}

/**
 * Overlay de metricas, solo con `?debug=1`.
 *
 * Existe porque el presupuesto de la Fase 2 (>=30 fps con throttling 6x, <=150 draw
 * calls, `fragments.update()` sin bloquear mas de 16 ms) hay que medirlo en la maquina
 * del cliente y con el throttling puesto desde DevTools, no a ojo en la de desarrollo.
 */
export class Depuracion {
  private readonly nodo: HTMLElement;
  private readonly marcas: number[] = [];
  private cuadro: MetricasCuadro = {
    drawCalls: 0,
    triangulos: 0,
    programas: 0,
    conSombra: false,
  };
  private peorSinSombra = 0;
  private peorConSombra = 0;
  private actualizacion: MetricasActualizacion = { bloqueoMs: 0, totalMs: 0 };
  private peorBloqueo = 0;
  private tareasLargas = 0;
  private observador: PerformanceObserver | null = null;
  private gpu = "desconocida";

  constructor(raiz: HTMLElement) {
    this.nodo = document.createElement("div");
    this.nodo.className = "debug";
    raiz.append(this.nodo);

    if (typeof PerformanceObserver !== "undefined") {
      try {
        this.observador = new PerformanceObserver((lista) => {
          this.tareasLargas += lista.getEntries().length;
        });
        this.observador.observe({ entryTypes: ["longtask"] });
      } catch {
        this.observador = null;
      }
    }
  }

  /** Lee el renderer real detras del contexto WebGL, que en hibridas no es obvio. */
  detectarGpu(gl: WebGL2RenderingContext | WebGLRenderingContext): void {
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    if (ext === null) return;
    const valor = gl.getParameter(ext.UNMASKED_RENDERER_WEBGL);
    if (typeof valor === "string") this.gpu = valor;
  }

  registrarCuadro(metricas: MetricasCuadro): void {
    this.cuadro = metricas;
    if (metricas.conSombra) {
      this.peorConSombra = Math.max(this.peorConSombra, metricas.drawCalls);
    } else {
      this.peorSinSombra = Math.max(this.peorSinSombra, metricas.drawCalls);
    }
    const ahora = performance.now();
    this.marcas.push(ahora);
    while (this.marcas.length > 0 && ahora - (this.marcas[0] ?? ahora) > 1000) {
      this.marcas.shift();
    }
    this.pintar();
  }

  registrarActualizacion(metricas: MetricasActualizacion): void {
    this.actualizacion = metricas;
    this.peorBloqueo = Math.max(this.peorBloqueo, metricas.bloqueoMs);
    this.pintar();
  }

  private pintar(): void {
    const fps = this.marcas.length;
    // El presupuesto se juzga contra el cuadro normal: el de sombra es esporadico,
    // solo cuando la geometria visible cambia, no en cada movimiento de camara.
    const presupuesto = this.peorSinSombra <= 150 ? "ok" : "EXCEDIDO";
    const bloqueo = this.peorBloqueo <= 16 ? "ok" : "EXCEDIDO";
    this.nodo.textContent = [
      `fps (cuadros/s)   ${fps}`,
      `draw calls        ${this.cuadro.drawCalls}${this.cuadro.conSombra ? "  (con pase de sombra)" : ""}`,
      `  peor sin sombra ${this.peorSinSombra}  (<=150 ${presupuesto})`,
      `  peor con sombra ${this.peorConSombra}`,
      `triangulos        ${
        Number.isFinite(this.cuadro.triangulos)
          ? this.cuadro.triangulos.toLocaleString("es")
          : "n/d"
      }`,
      `programas         ${this.cuadro.programas}`,
      `update bloqueo    ${this.actualizacion.bloqueoMs.toFixed(1)} ms  (peor ${this.peorBloqueo.toFixed(1)}, <=16 ${bloqueo})`,
      `update total      ${this.actualizacion.totalMs.toFixed(1)} ms`,
      `long tasks >50ms  ${this.tareasLargas}`,
      `gpu               ${this.gpu}`,
    ].join("\n");
  }

  dispose(): void {
    this.observador?.disconnect();
    this.observador = null;
    this.nodo.remove();
  }
}
